import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconTestPipe } from '@tabler/icons-react';

import { formatToDateAndTime, Typography } from '@zendesk/forethought-elements';
import { formatAbTestDatetime } from './ab-test-config/utils';
import { SolveABTestConfig } from 'src/services/agent-ab-test/types';

export default function Banner({ abTest }: { abTest: SolveABTestConfig }) {
  const { palette } = useTheme();
  const { t } = useTranslation('agent-ab-test');

  if (abTest.status === 'draft') {
    return null;
  }

  function getColor() {
    if (abTest.status === 'running') {
      return palette.colors.green[500];
    }
    if (abTest.status === 'canceled') {
      return palette.error.main;
    }
    return palette.colors.blue[500];
  }

  function getText() {
    if (abTest.status === 'running') {
      return abTest.end_condition?.condition_type === 'fixed_duration' &&
        abTest.end_condition?.end_datetime
        ? t('banner.runningEndsOnDate', {
            endDate: formatAbTestDatetime(
              abTest.end_condition?.end_datetime ?? '',
              abTest.timezone,
            ),
          })
        : t('banner.runningEndsAfterConversations', {
            count: abTest.end_condition?.sample_count || 0,
          });
    }
    if (abTest.status === 'canceled') {
      return t('banner.canceled', {
        date: formatToDateAndTime(abTest.modified_date),
      });
    }
    return t('banner.completed');
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: getColor(),
        display: 'flex',
        gap: '8px',
        height: '40px',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: palette.background.default,
          borderRadius: '50%',
          display: 'flex',
          height: '24px',
          justifyContent: 'center',
          width: '24px',
        }}
      >
        <IconTestPipe color={palette.text.primary} size={15} />
      </Box>
      <Typography color={palette.background.default} variant='font14Bold'>
        {getText()}
      </Typography>
    </Box>
  );
}
